/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Popover from '@material-ui/core/Popover';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Chip from '@ion/dls/src/components/chip/Chip';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsAlt, dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import ClickAwayListener from '../components/listeners/ClickAwayListener';
import {useChipDisplay} from '../hooks/useChipDisplay';
import {FilterHashMapKeyType} from './FilterHelpers';
import RenderChips from './RenderChips';
import {getFilterSelection} from './utils';

export interface FilterSelectedOptionsTitleProps {
  optionType: string;
  selectedOptionsCount: number;
  showSelectedOptionsCount: boolean;
}

export interface FilterSelectedOptionsProps {
  selectedOptions: string[];
  selectedOptionsCount: number;
  optionData?: FilterHashMapKeyType;
  numberChipsLimit?: number;
  shouldDisplayDateChip?: boolean;
  shouldDisplayBoundaryChip?: boolean;
  shouldDisplayFindNearbyChip?: boolean;
  handleClearDateChip?: () => void;
  handleClearBoundaryChip?: Function;
  handleClearFindNearbyChip?: Function;
  onChipDelete: Function;
  clearAll: Function;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  titleContainer : {
    [theme.breakpoints.down('sm')] : {
      marginBottom : '10px'
    }
  },
  title : {
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    fontWeight    : 'bold',
    fontSize      : '14px',
    letterSpacing : '1px'
  },
  titleCount : {
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey4'),
    fontWeight    : 'bold',
    fontSize      : '14px',
    letterSpacing : '1px',
    padding       : '0px 10px 0px 5px'
  },
  rowContainer : {
    maxWidth                       : '88%',
    display                        : 'flex',
    flex                           : 1,
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      width    : '100%',
      maxWidth : '100% !important'
    }
  },
  more : {
    ...dlsAlt('typography', 'label3'),
    marginLeft : 8
  },
  moreFilter : {
    display         : 'flex',
    whiteSpace      : 'nowrap',
    cursor          : 'pointer',
    paddingRight    : 4,
    color           : dlsColor('grey3'),
    backgroundColor : 'white'
  },
  moreFilterItems : {
    padding       : 20,
    minWidth      : 160,
    display       : 'flex',
    flexDirection : 'column',
    '&& > *'      : {
      margin : '5px 0'
    }
  },
  chipContainer : {
    display    : 'flex',
    alignItems : 'center',
    maxWidth   : '92%'
  },
  clearAll : {
    ...dlsAlt('components', 'ionToggle', 'ionBeta'),
    fontWeight     : 700,
    color          : dlsColor('aaBlue'),
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'flex-end',
    padding        : '0 8px',
    opacity        : '1',
    cursor         : 'pointer',
    whiteSpace     : 'nowrap'
  }
}));

/**
 * Provides selected option text and optionally includes the number of
 * currently selected options in parentheses if showSelectedOptionsCount
 * is set to true. Resulting in: "SELECTED optionType (selectedOptionsCount)"
 *
 * ```typescript
 *
 * import FilterSelectedOptionsTitle from './FilterSelectedOptionsTitle';
 *
 * <FilterSelectedOptionsTitle optionType={optionType}
 *   selectedOptionsCount={filterSelectedOptions.length}
 *   showSelectedOptionsCount={showSelectedOptionsCount} />
 *
 * ```
 */
export const FilterSelectedOptionsTitle = ({
  optionType,
  selectedOptionsCount,
  showSelectedOptionsCount
}: FilterSelectedOptionsTitleProps) => {
  const classes = useStyles();

  const renderTitle = () => {
    if (selectedOptionsCount > 0) {
      return `${translate('common:filters.selected')} ${optionType}`;
    }
    return '';
  };

  const renderCount = () => {
    if (selectedOptionsCount > 0 && showSelectedOptionsCount) {
      return `(${selectedOptionsCount})`;
    }
    return '';
  };

  return (
    <div className={classes.titleContainer}>
      <span className={classes.title}>{renderTitle()}</span>
      <span className={classes.titleCount}>{renderCount()}</span>
    </div>
  );
};

/**
 * Displays a Chip component for each item in the optionItems prop up to
 * a specified number (default = 5). If there are more items than this
 * number, the string "+ NN MORE' is appended to the end of the line.
 *
 * ```typescript
 *
 * import FilterSelectedOptions from './FilterSelectedOptions';
 *
 *  <FilterSelectedOptions selectedOptions={filterSelectedOptions}
 *    selectedOptionsCount={filterSelectedOptionsCount}
 *    optionData={filterOrderedMap}
 *    numberChipsLimit={numberChipsLimit}
 *    onChipDelete={onChipDelete} />
 *
 * ```
 */
export const FilterSelectedOptions = ({
  selectedOptions,
  optionData,
  onChipDelete,
  shouldDisplayDateChip,
  shouldDisplayBoundaryChip,
  shouldDisplayFindNearbyChip,
  handleClearDateChip,
  handleClearBoundaryChip,
  handleClearFindNearbyChip,
  numberChipsLimit = 5,
  clearAll
}: FilterSelectedOptionsProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl]         = useState(null);
  const {filterSelected, selectedCount} = useMemo(() => getFilterSelection(selectedOptions), [selectedOptions]);
  const containerRef                    = useRef(null);
  const isNumberChipsLimitZero          = numberChipsLimit === 0;

  const {
    visibleChips,
    hiddenChips,
    hidden
  } =  useChipDisplay({
    filterSelected,
    optionData,
    containerRef,
    numberChipsLimit,
    shouldDisplayBoundaryChip,
    shouldDisplayFindNearbyChip,
    shouldDisplayDateChip,
    sampleChip : <Chip customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />} />
  });

  const moreFiltersCount = `+ ${numberChipsLimit === 0 ? hidden : selectedCount - numberChipsLimit} ${translate('common:filters.more')}`;

  useEffect(() => {
    if (selectedCount <= (isNumberChipsLimitZero ? hidden : numberChipsLimit) && Boolean(anchorEl)) {
      setAnchorEl(null);
    }
  }, [selectedCount]);

  const onClickAway = useCallback(function() {
    setAnchorEl(null);
  }, []);

  const onDeleteDateChip = useCallback(function() {
    handleClearDateChip();
  }, []);

  const onDeleteBoundaryChip = useCallback(function() {
    handleClearBoundaryChip();
  }, []);

  const onDeleteFindNearbyChip = useCallback(function() {
    handleClearFindNearbyChip?.(true);
  }, []);

  return (
    <div ref={containerRef} className={classes.rowContainer}>
      <div id='chipsWrapper' className={classes.chipContainer}>
        {shouldDisplayDateChip && (
          <Chip id='date-chip'
            label={translate('common:time.dates')}
            customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
            borderRadius='4px'
            labelColor={dlsColor('grey2')}
            maxWidth={false}
            onDelete={onDeleteDateChip}
            onClick={onDeleteDateChip}
            size='small'
            showCustomDeleteIcon
            hoverDefault />
        )}
        {shouldDisplayBoundaryChip && (
          <Chip id='boundary-chip'
            label={translate('common:filters.boundary')}
            customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
            borderRadius='4px'
            labelColor={dlsColor('grey2')}
            maxWidth={false}
            onDelete={onDeleteBoundaryChip}
            onClick={onDeleteBoundaryChip}
            size='small'
            showCustomDeleteIcon
            hoverDefault />
        )}
        {shouldDisplayFindNearbyChip && (
          <Chip id='findNearby-chip'
            label={translate('common:filters.findNearby')}
            customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
            borderRadius='4px'
            labelColor={dlsColor('grey2')}
            maxWidth={false}
            onDelete={onDeleteFindNearbyChip}
            onClick={onDeleteFindNearbyChip}
            size='small'
            showCustomDeleteIcon
            hoverDefault />
        )}
        
        <RenderChips chips={isNumberChipsLimitZero ? visibleChips : filterSelected}
          optionData={optionData}
          onChipDelete={onChipDelete}
          numberChipsLimit={numberChipsLimit}
          renderIconChips={isNumberChipsLimitZero} />

        <Popover style={{zIndex: dlsZIndex('desktopFilterPopover')}}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical   : 'bottom',
            horizontal : 'right'
          }}
          transformOrigin={{
            vertical   : 'top',
            horizontal : 'right'
          }}>
          <ClickAwayListener onClickAway={onClickAway}
            mountListener>
            <div className={classes.moreFilterItems}>
              <RenderChips chips={isNumberChipsLimitZero ? hiddenChips : filterSelected}
                optionData={optionData}
                onChipDelete={onChipDelete}
                numberChipsLimit={numberChipsLimit}
                renderIconChips={isNumberChipsLimitZero}
                additionalCondition={isNumberChipsLimitZero ? !!hidden : true}
                renderMoreItems />
            </div>
          </ClickAwayListener>
        </Popover>
        {(isNumberChipsLimitZero ? hidden > 0 : selectedCount > numberChipsLimit)
          ? <div className={classes.moreFilter} id='moreFilter' onClick={function({currentTarget}) {
            setAnchorEl(anchorEl ? null : currentTarget);
          }}>
            <span className={classes.more}>{moreFiltersCount}</span>
          </div>
          : null}
      </div>
      {clearAll &&
      <div className={classes.clearAll}>
        <span onClick={clearAll}>
          {translate('nav:filters.expanded.clearAll')}
        </span>
      </div>
      }
    </div>
  );
};

export default FilterSelectedOptions;
